/**
 * Permission management utilities for Cognito group-based access control
 * Extracts user groups from Cognito ID token and provides permission checking functions
 */

/**
 * Decode JWT token to extract payload
 * @param {string} token - JWT token string
 * @returns {Object|null} Decoded token payload or null if invalid
 */
const decodeToken = (token) => {
  try {
    if (!token) return null;
    
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

/**
 * Extract user groups from Cognito ID token
 * Groups are stored in the 'cognito:groups' claim
 * @param {string} idToken - Cognito ID token
 * @returns {string[]} Array of group names
 */
export const getUserGroups = (idToken) => {
  if (!idToken) return [];
  
  const tokenString = typeof idToken === 'string' ? idToken : idToken.toString();
  const payload = decodeToken(tokenString);
  
  if (!payload) return [];
  
  // Cognito groups are in the 'cognito:groups' claim
  const groups = payload['cognito:groups'] || [];
  return Array.isArray(groups) ? groups : [];
};

/**
 * Check if user has a specific group
 * @param {string} idToken - Cognito ID token
 * @param {string} groupName - Group name to check (case-insensitive)
 * @returns {boolean} True if user is in the group
 */
export const hasGroup = (idToken, groupName) => {
  const groups = getUserGroups(idToken);
  return groups.some(
    (group) => group.toLowerCase() === groupName.toLowerCase()
  );
};

/**
 * Check if user has any of the specified groups
 * @param {string} idToken - Cognito ID token
 * @param {string[]} groupNames - Array of group names to check
 * @returns {boolean} True if user is in any of the groups
 */
export const hasAnyGroup = (idToken, groupNames) => {
  if (!groupNames || groupNames.length === 0) return false;
  
  const groups = getUserGroups(idToken);
  const lowerGroups = groups.map((g) => g.toLowerCase());
  
  return groupNames.some((groupName) =>
    lowerGroups.includes(groupName.toLowerCase())
  );
};

/**
 * Check if user has all of the specified groups
 * @param {string} idToken - Cognito ID token
 * @param {string[]} groupNames - Array of group names to check
 * @returns {boolean} True if user is in all of the groups
 */
export const hasAllGroups = (idToken, groupNames) => {
  if (!groupNames || groupNames.length === 0) return false;
  
  const groups = getUserGroups(idToken);
  const lowerGroups = groups.map((g) => g.toLowerCase());
  
  return groupNames.every((groupName) =>
    lowerGroups.includes(groupName.toLowerCase())
  );
};

/**
 * Permission definitions - map groups to permissions
 * You can customize this based on your needs
 */
export const PERMISSIONS = {
  // Admin permissions
  ADMIN: {
    VIEW_ALL: true,
    EDIT_ALL: true,
    DELETE_ALL: true,
    MANAGE_USERS: true,
    MANAGE_SETTINGS: true,
    APPROVE_EDITS: true,
    APPROVE_NEW_ITEMS: true,
    REVIEW_IMAGES: true,
    MANAGE_ADVERTISING: true,
  },
  
  // Manager permissions
  MANAGER: {
    VIEW_ALL: true,
    EDIT_ALL: true,
    DELETE_ALL: false,
    MANAGE_USERS: false,
    MANAGE_SETTINGS: false,
    APPROVE_EDITS: true,
    APPROVE_NEW_ITEMS: true,
    REVIEW_IMAGES: true,
    MANAGE_ADVERTISING: false,
  },
};

/**
 * Check if user has a specific permission
 * @param {string} idToken - Cognito ID token
 * @param {string} permission - Permission name (e.g., 'EDIT_ALL', 'APPROVE_EDITS')
 * @returns {boolean} True if user has the permission
 */
export const hasPermission = (idToken, permission) => {
  const groups = getUserGroups(idToken);
  
  // Check each group for the permission
  for (const group of groups) {
    const groupUpper = group.toUpperCase();
    if (PERMISSIONS[groupUpper] && PERMISSIONS[groupUpper][permission]) {
      return true;
    }
  }
  
  return false;
};

/**
 * Get all permissions for a user based on their groups
 * @param {string} idToken - Cognito ID token
 * @returns {Object} Object with permission names as keys and boolean values
 */
export const getUserPermissions = (idToken) => {
  const groups = getUserGroups(idToken);
  const permissions = {};
  
  // Collect all permissions from user's groups
  for (const group of groups) {
    const groupUpper = group.toUpperCase();
    if (PERMISSIONS[groupUpper]) {
      Object.assign(permissions, PERMISSIONS[groupUpper]);
    }
  }
  
  return permissions;
};

